import { useEffect, useState } from "react";
import { BarChart3, Loader2, Pencil, Trash2, Target, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const WINDOWS = [
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "ytd", label: "YTD" },
  { key: "12mo", label: "12mo" },
  { key: "all", label: "All" },
];

const money = (n) =>
  "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Deterministic color per category name so re-renders don't re-shuffle colors.
const PALETTE = [
  "bg-slate-900", "bg-emerald-600", "bg-violet-600", "bg-amber-600",
  "bg-blue-600", "bg-rose-600", "bg-teal-600", "bg-indigo-600",
  "bg-lime-600", "bg-orange-600",
];
function colorFor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return PALETTE[h % PALETTE.length];
}

// Budget utilization visual style per warning band.
const BAND_STYLES = {
  ok:     { bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "On track" },
  warn:   { bar: "bg-amber-500",   chip: "bg-amber-50 text-amber-700 border-amber-200",       label: "Approaching" },
  danger: { bar: "bg-orange-600",  chip: "bg-orange-50 text-orange-800 border-orange-300",    label: "Near limit" },
  over:   { bar: "bg-red-600",     chip: "bg-red-50 text-red-700 border-red-300",             label: "Over budget" },
};

/**
 * Vendor spend-by-category analytics card with annual budget-vs-actual
 * tracking. Renders: (a) per-category horizontal spend bars for the selected
 * window, (b) YTD utilization meter against the category's annual budget with
 * warning bands (75%/90%/100%+), (c) inline set-budget button on every row.
 *
 * Backend: GET /api/vendors/analytics/spend-by-category?window=...
 *          PUT/DELETE /api/vendors/budgets/{category}?year=YYYY
 */
export default function VendorSpendAnalytics() {
  const [data, setData] = useState(null);
  const [windowKey, setWindowKey] = useState("90d");
  const [busy, setBusy] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // category name being edited
  const [editAmount, setEditAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async (w) => {
    setBusy(true);
    try {
      const res = await api.get(`/vendors/analytics/spend-by-category?window=${w}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load spend analytics");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(windowKey); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [windowKey]);

  const openEdit = (row) => {
    setEditingCat(row.category);
    setEditAmount(row.annual_budget != null ? String(row.annual_budget) : "");
  };
  const cancelEdit = () => { setEditingCat(null); setEditAmount(""); };

  const saveBudget = async (category) => {
    const raw = (editAmount ?? "").trim();
    const n = Number(raw);
    if (raw === "" || Number.isNaN(n) || n < 0) {
      toast.error("Enter a positive dollar amount (or 0 to clear)");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/vendors/budgets/${encodeURIComponent(category)}`, { annual_budget: n });
      toast.success(`Annual budget set for ${category}`);
      cancelEdit();
      await load(windowKey);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save budget");
    } finally { setSaving(false); }
  };

  const clearBudget = async (category) => {
    if (!confirm(`Remove the annual budget for "${category}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/vendors/budgets/${encodeURIComponent(category)}`);
      toast.success("Budget removed");
      cancelEdit();
      await load(windowKey);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove budget");
    } finally { setSaving(false); }
  };

  const rows = data?.rows || [];
  const max = rows.reduce((m, r) => Math.max(m, Number(r.spend) || 0), 0);
  const overBudgetCount = rows.filter((r) => r.budget_band === "over").length;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-5" data-testid="vendor-spend-analytics">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <BarChart3 className="w-4 h-4 text-slate-700" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Spend by category
            </h2>
          </div>
          <p className="text-xs text-slate-500">
            Vendor spend grouped by category — combines work-order costs (actual, falling back to estimated for open jobs) and direct expenses tagged to a vendor.
          </p>
        </div>
        <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden bg-white" role="group" data-testid="vendor-spend-window-group">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              className={`px-3 py-1 text-xs font-semibold transition ${
                windowKey === w.key ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
              } ${w.key !== "30d" ? "border-l border-slate-300" : ""}`}
              data-testid={`vendor-spend-window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4" data-testid="vendor-spend-kpis">
        <Kpi label="Window spend" value={money(data?.total_spend || 0)} bgClass="bg-green-50 border-green-200" />
        <Kpi
          label="Source mix"
          value={`${data?.total_work_orders ?? 0} WO · ${data?.total_expenses ?? 0} exp`}
          hint="Work orders + direct expenses with vendor"
          bgClass="bg-purple-50 border-purple-200"
        />
        <Kpi label="YTD spend" value={money(data?.total_ytd_spend || 0)} hint={data?.year ? `Jan 1 ${data.year}` : ""} bgClass="bg-amber-50 border-amber-200" />
        <Kpi
          label="Annual budget"
          value={money(data?.total_annual_budget || 0)}
          hint={overBudgetCount > 0 ? `${overBudgetCount} over budget` : ""}
          tone={overBudgetCount > 0 ? "alert" : undefined}
          bgClass="bg-green-50 border-green-200"
        />
      </div>

      {busy ? (
        <div className="py-8 text-center text-sm text-slate-400 flex items-center gap-2 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-sm" data-testid="vendor-spend-empty">
          No vendor spend in this window yet. Log a cost on a closed work order, or attach a vendor when logging an expense, to see data here.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="vendor-spend-rows">
          {rows.map((r) => (
            <SpendRow
              key={r.category}
              row={r}
              max={max}
              editing={editingCat === r.category}
              editAmount={editAmount}
              setEditAmount={setEditAmount}
              onEdit={() => openEdit(r)}
              onCancel={cancelEdit}
              onSave={() => saveBudget(r.category)}
              onClear={() => clearBudget(r.category)}
              saving={saving}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SpendRow({ row, max, editing, editAmount, setEditAmount, onEdit, onCancel, onSave, onClear, saving }) {
  const pct = max > 0 ? Math.max(2, (row.spend / max) * 100) : 0;
  const hasBudget = row.annual_budget != null && row.annual_budget > 0;
  const util = row.budget_utilization_pct ?? 0;
  const band = row.budget_band;
  const bandStyle = band ? BAND_STYLES[band] : null;
  const meterPct = Math.min(100, util);
  return (
    <li className="group" data-testid={`vendor-spend-row-${row.category}`}>
      <div className="flex items-center justify-between gap-2 mb-1 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-slate-900 truncate">{row.category}</span>
          {bandStyle && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold border ${bandStyle.chip}`}
              data-testid={`vendor-spend-band-${row.category}`}
            >
              {band === "over" && <AlertTriangle className="w-3 h-3" />}
              {bandStyle.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 font-mono whitespace-nowrap">
          <span className="text-slate-900 font-bold">{money(row.spend)}</span>
          <span className="text-slate-400">
            {row.work_order_count > 0 && (
              <>{row.work_order_count} WO{row.work_order_count === 1 ? "" : "s"}</>
            )}
            {row.work_order_count > 0 && (row.expense_count || 0) > 0 && " · "}
            {(row.expense_count || 0) > 0 && (
              <>{row.expense_count} exp</>
            )}
            {row.planned_only_count > 0 && (
              <span className="ml-1 text-amber-600" title="Some work orders only have estimated (not actual) cost yet">
                · {row.planned_only_count} planned
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Window spend bar */}
      <div className="h-2 w-full rounded-sm bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${colorFor(row.category)} transition-[width] duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Budget utilization meter + edit row */}
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
        {hasBudget ? (
          <>
            <div className="flex-1 flex items-center gap-2 min-w-0">
              <span className="text-slate-500 font-mono whitespace-nowrap">
                YTD {money(row.ytd_spend)} / {money(row.annual_budget)}
              </span>
              <div className="flex-1 h-1.5 rounded-sm bg-slate-100 overflow-hidden min-w-[40px]" data-testid={`vendor-spend-meter-${row.category}`}>
                <div
                  className={`h-full ${bandStyle?.bar || "bg-emerald-500"} transition-[width] duration-500`}
                  style={{ width: `${meterPct}%` }}
                />
              </div>
              <span className={`font-mono font-semibold whitespace-nowrap ${band === "over" ? "text-red-700" : band === "danger" ? "text-orange-700" : "text-slate-600"}`}>
                {util.toFixed(1)}%
              </span>
            </div>
            {!editing && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  type="button"
                  onClick={onEdit}
                  className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                  title="Edit annual budget"
                  data-testid={`vendor-spend-edit-${row.category}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={onClear}
                  className="p-1 rounded hover:bg-red-50 text-slate-500 hover:text-red-700"
                  title="Remove annual budget"
                  data-testid={`vendor-spend-clear-${row.category}`}
                  disabled={saving}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </>
        ) : (
          !editing && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-[11px] font-semibold"
              data-testid={`vendor-spend-set-budget-${row.category}`}
            >
              <Target className="w-3 h-3" /> Set annual budget
            </button>
          )
        )}
      </div>

      {editing && (
        <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`vendor-spend-edit-row-${row.category}`}>
          <span className="text-slate-500">Annual budget&nbsp;$</span>
          <input
            type="number"
            min="0"
            step="100"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            className="w-36 px-2 py-1 border border-slate-300 rounded-sm font-mono text-right focus:outline-none focus:ring-1 focus:ring-slate-900"
            autoFocus
            data-testid={`vendor-spend-edit-input-${row.category}`}
          />
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="px-3 py-1 bg-slate-900 text-white rounded-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
            data-testid={`vendor-spend-edit-save-${row.category}`}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1 border border-slate-300 text-slate-700 rounded-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            data-testid={`vendor-spend-edit-cancel-${row.category}`}
          >
            Cancel
          </button>
          {hasBudget && (
            <button
              type="button"
              onClick={onClear}
              disabled={saving}
              className="ml-auto text-red-600 hover:text-red-800 font-semibold"
              data-testid={`vendor-spend-edit-remove-${row.category}`}
            >
              Remove
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function Kpi({ label, value, hint, tone, bgClass }) {
  const base = bgClass || (tone === "alert" ? "border-red-300 bg-red-50" : "bg-white border-slate-200");
  return (
    <div className={`border rounded-sm p-3 ${base}`}>
      <div className="overline text-slate-500 mb-0.5">{label}</div>
      <div className={`font-mono font-bold text-lg ${tone === "alert" ? "text-red-700" : "text-slate-900"}`}>{value}</div>
      {hint && <div className={`text-[10px] mt-0.5 ${tone === "alert" ? "text-red-700" : "text-slate-400"}`}>{hint}</div>}
    </div>
  );
}
